import { Sun, Moon, Monitor } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

/* ─────────────────────────────────────────────────────────────────────────────
   ThemeToggle — a three-way segmented control: Light · System · Dark.
   "System" follows the device setting and keeps following it if the phone
   or laptop switches over at sunset.

   variant="bar"   compact, for the account menu
   variant="rail"  vertical, for the 60px desktop icon rail — on every page
   variant="panel" full width with labels, for the dark navy mobile drawer
───────────────────────────────────────────────────────────────────────────── */

const OPTIONS = [
  { value: "light",  icon: Sun,     label: "Light",  hint: "Always light" },
  { value: "system", icon: Monitor, label: "System", hint: "Match my device" },
  { value: "dark",   icon: Moon,    label: "Dark",   hint: "Always dark" },
];

export default function ThemeToggle({ variant = "bar", className = "" }) {
  const { preference, setPreference } = useTheme();
  const panel = variant === "panel";

  /* The rail variant is styled in command-center.css so it sits in the
     icon rail's own visual language (same plates, same tooltips). */
  if (variant === "rail") {
    return (
      <div role="radiogroup" aria-label="Colour theme" data-testid="theme-toggle" className={`ivcc-theme ${className}`}>
        {OPTIONS.map(({ value, icon: Icon, label, hint }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={preference === value}
            aria-label={`${label} theme — ${hint}`}
            onClick={() => setPreference(value)}
            data-testid={`theme-${value}`}
            className={`ivcc-theme-b${preference === value ? " on" : ""}`}
          >
            <Icon strokeWidth={1.9} />
            <span className="ivcc-tip">{label} · {hint}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      data-testid="theme-toggle"
      className={`inline-flex items-center gap-0.5 rounded-full p-0.5 ${
        panel
          ? "w-full border border-white/10 bg-white/5"
          : "border border-border bg-muted/60"
      } ${className}`}
    >
      {OPTIONS.map(({ value, icon: Icon, label, hint }) => {
        const on = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={`${label} theme — ${hint}`}
            title={`${label} · ${hint}`}
            onClick={() => setPreference(value)}
            data-testid={`theme-${value}`}
            className={`flex items-center justify-center gap-1.5 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              panel ? "h-9 flex-1 text-xs font-semibold" : "h-7 w-7"
            } ${
              panel
                ? on
                  ? "bg-white/15 text-white"
                  : "text-white/55 hover:text-white"
                : on
                  ? "bg-card text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className={panel ? "h-4 w-4" : "h-[15px] w-[15px]"} />
            {panel && <span>{label}</span>}
          </button>
        );
      })}
    </div>
  );
}
